import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Webcam from 'react-webcam';

import Spinner from '../../../shared/_TBComponents/Spinner';
import NoHoverButton from '../../../shared/components/NoHoverButton';

import { ColorGreen, ColorWhite } from '../../../shared/GlobalStyles';

import { setSubActiveReduxModal } from '../ducks/journalActions';

const SpinnerWrapper = styled('div')`
  align-items: center;
  display: flex;
  height: 395px;
  justify-content: center;
  width: 700px;
`;

const WebcamContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-family: 'Nunito Sans';
  height: 100%;
  justify-content: center;
  width: 100%;
  .capture-button {
    background-color: ${ColorGreen};
    color: ${ColorWhite};
    width: 180px;
    &:hover {
      opacity: 0.7;
    }
  }
  .modal-button {
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
  }
  .text {
    font-size: 26px;
    font-weight: 300;
  }
  .webcam {
    border-radius: 24px;
  }
`;

const videoConstraints = {
  facingMode: 'user',
  height: 720,
  width: 1280,
};

const WebcamCapture = ({
  handleUploadMedia,
  amplitudeFunction,
  mediaType,
}) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const commentMediaTarget = useSelector((state) => state.journal.data.commentMediaTarget);
  const isMediaLoading = useSelector((state) => state.journal.ui.isMediaLoading);

  const dispatch = useDispatch();
  const mediaRecorderRef = useRef(null);
  const webcamRef = useRef(null);

  const isVideo = mediaType === 'video';
  const [capturing, setCapturing] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);
  const [isWebcamReady, setIsWebcamReady] = useState(false);

  const uploadFile = useCallback((fileArray, type) => {
    if (!commentMediaTarget.entryId) {
      dispatch(handleUploadMedia({
        accountCode: currentUser.accountCode,
        acceptedFile: fileArray,
        type,
      }));
    } else {
      dispatch(handleUploadMedia({
        accountCode: currentUser.accountCode,
        acceptedFile: fileArray,
        type,
      }, commentMediaTarget.entryId, commentMediaTarget.commentId));
    }
    amplitudeFunction();
  }, [commentMediaTarget, currentUser.accountCode, dispatch, handleUploadMedia, amplitudeFunction]);

  const handleImageCapture = useCallback(() => {
    const imageSrc = webcamRef?.current?.getScreenshot();
    const imageBuff = Buffer.from(
      imageSrc?.replace(/^data:image\/\w+;base64,/, ''),
      'base64',
    );
    const image = new File([imageBuff], 'jpg', {
      type: 'image/jpeg',
    });
    const imgArray = [image];
    setIsCaptured(true);
    uploadFile(imgArray, 'image');
  }, [uploadFile]);

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      setIsCaptured(true);
      setCapturing(false);
      const file = new File([data], 'mp4', {
        type: 'video/mp4',
      });
      const fileArray = [file];
      const type = fileArray[0].type.split('/').shift();
      uploadFile(fileArray, type);
    }
  }, [uploadFile]);

  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true);
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream);
    mediaRecorderRef.current.addEventListener('dataavailable', handleDataAvailable);
    mediaRecorderRef.current.start();
  }, [handleDataAvailable]);

  const handleStopCaptureClick = useCallback(() => {
    mediaRecorderRef.current.stop();
  }, []);

  const handleClose = useCallback(() => {
    dispatch(setSubActiveReduxModal(''));
  }, [dispatch]);

  const handleUserMedia = useCallback(() => {
    setIsWebcamReady(true);
  }, []);

  const handleUserMediaError = useCallback(() => {
    setIsWebcamReady(false);
  }, []);

  const renderButton = () => {
    let cta;
    let onClick;
    let disabled = false;
    let ariaLabel;

    if (isCaptured) {
      cta = 'Done';
      onClick = handleClose;
      ariaLabel = 'Close and finish';
    } else if (isVideo && capturing) {
      cta = 'Stop Capture';
      onClick = handleStopCaptureClick;
      ariaLabel = 'Stop video recording';
    } else if (isVideo) {
      cta = 'Start Capture';
      onClick = handleStartCaptureClick;
      disabled = !isWebcamReady;
      ariaLabel = 'Start video recording';
    } else {
      cta = 'Take Photo';
      onClick = handleImageCapture;
      disabled = !isWebcamReady;
      ariaLabel = 'Take photo';
    }

    return (
      <NoHoverButton
        aria-label={ariaLabel}
        bottom
        className='modal-button'
        cta={cta}
        customColor='#10cd8c'
        disabled={disabled}
        fullWidth
        large
        noBorder
        noHover
        onClick={onClick}
        primary
        square
      />
    );
  };

  const renderWebcam = () => {
    if (isCaptured && isMediaLoading) {
      return (
        <SpinnerWrapper>
          <Spinner large aria-label='Uploading media...' />
        </SpinnerWrapper>
      );
    }

    const baseProps = {
      'aria-label': 'Webcam preview',
      className: 'webcam',
      height: 395,
      ref: webcamRef,
      videoConstraints,
      width: 700,
      onUserMedia: handleUserMedia,
      onUserMediaError: handleUserMediaError,
    };

    if (isVideo) {
      return <Webcam {...baseProps} />;
    }

    return (
      <Webcam
        {...baseProps}
        audio={false}
        screenshotFormat='image/jpeg'
        screenshotQuality={1}
      />
    );
  };

  return (
    <WebcamContainer>
      {renderWebcam()}
      {renderButton()}
    </WebcamContainer>
  );
};

WebcamCapture.propTypes = {
  handleUploadMedia: PropTypes.func.isRequired,
  amplitudeFunction: PropTypes.func.isRequired,
  mediaType: PropTypes.oneOf(['image', 'video']).isRequired,
};

export default WebcamCapture;
